import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { getUserProfile, updateUserProfile, changePassword, UserProfile } from '@/services/profile';
import { useClientAuthStore } from '@/store/useClientAuthStore';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useClientAuthStore();

  // Estados para el perfil
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState('');

  // Estados para cambio de contraseña
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Estados para validación y feedback
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [newPasswordError, setNewPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Cargar perfil del usuario
  useEffect(() => {
    const loadProfile = async () => {
      try {
        const userProfile = await getUserProfile();
        setProfile(userProfile);
      } catch (error) {
        setProfileError('Error al cargar el perfil');
        console.error('Error loading profile:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfile();
  }, []);

  // Validaciones para contraseña
  const validateNewPassword = (password: string): boolean => {
    if (!password) {
      setNewPasswordError('La nueva contraseña es requerida');
      return false;
    }
    if (password.length < 8) {
      setNewPasswordError('La contraseña debe tener al menos 8 caracteres');
      return false;
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      setNewPasswordError('La contraseña debe contener al menos una mayúscula, una minúscula y un número');
      return false;
    }
    setNewPasswordError('');
    return true;
  };

  const validateConfirmPassword = (confirmPassword: string): boolean => {
    if (!confirmPassword) {
      setConfirmPasswordError('Confirmar la contraseña es requerido');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setConfirmPasswordError('Las contraseñas no coinciden');
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  // Manejar cambio de contraseña
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');

    // Validaciones
    if (!currentPassword) {
      setPasswordError('La contraseña actual es requerida');
      return;
    }

    const isNewPasswordValid = validateNewPassword(newPassword);
    const isConfirmPasswordValid = validateConfirmPassword(confirmPassword);

    if (!isNewPasswordValid || !isConfirmPasswordValid) {
      return;
    }

    setIsChangingPassword(true);

    try {
      await changePassword(currentPassword, newPassword);

      // Mostrar mensaje de éxito
      setPasswordChangeSuccess(true);
      setShowSuccessModal(true);

      // Limpiar formulario
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowPasswordSection(false);

      // Limpiar errores
      setPasswordError('');
      setNewPasswordError('');
      setConfirmPasswordError('');

      // El modal se auto-cierra después de 3 segundos si el usuario no interactúa
      setTimeout(() => {
        if (showSuccessModal) {
          setShowSuccessModal(false);
        }
      }, 5000);

    } catch (error: any) {

      // Manejar diferentes tipos de errores
      let errorMessage = 'Error al cambiar la contraseña';

      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      // Si el error menciona que la contraseña actual es incorrecta
      if (errorMessage.toLowerCase().includes('contraseña actual') ||
          errorMessage.toLowerCase().includes('current password') ||
          errorMessage.toLowerCase().includes('incorrecta') ||
          errorMessage.toLowerCase().includes('incorrect')) {
        setPasswordError('La contraseña actual es incorrecta');
      } else {
        setPasswordError(errorMessage);
      }
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Manejar navegación después del cambio exitoso
  const handlePostPasswordChange = (destination: 'home' | 'login') => {
    setShowSuccessModal(false);
    if (destination === 'login') {
      logout();
      navigate('/login');
    } else {
      navigate('/home');
    }
  };

  if (isLoadingProfile) {
    return (
      <div className="p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-secondary">Cargando perfil...</p>
        </div>
      </div>
    );
  }

  if (profileError && !profile) {
    return (
      <div className="p-4">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-red-600 mb-2">Error</h3>
          <p className="text-secondary">{profileError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-4xl mx-auto">
      {/* Modal de éxito */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircleIcon className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-primary mb-2">¡Contraseña cambiada!</h2>
              <p className="text-secondary mb-6">
                Tu contraseña ha sido actualizada exitosamente. ¿Qué deseas hacer ahora?
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => handlePostPasswordChange('home')}
                  className="w-full btn-primary py-2 px-4"
                >
                  Continuar en la aplicación
                </button>
                <button
                  onClick={() => handlePostPasswordChange('login')}
                  className="w-full btn-secondary py-2 px-4"
                >
                  Ir a iniciar sesión
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-primary mb-2">Mi Perfil</h1>
        <p className="text-secondary">Gestiona tu información personal y configuración</p>
      </div>

      {/* Información del perfil */}
      <div className="card mb-6">
        <div className="flex items-center space-x-4 mb-4">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full">
            <UserIcon className="w-8 h-8 text-primary-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-primary">{profile?.fullName || 'Usuario'}</h2>
            <p className="text-secondary">{profile?.email}</p>
          </div>
          <button className="btn-secondary py-2 px-3">
            <PencilIcon className="w-4 h-4 mr-1" />
            Editar
          </button>
        </div>

        {profile && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium text-primary">Teléfono:</span>
              <span className="ml-2 text-secondary">{profile.phone || 'No especificado'}</span>
            </div>
            <div>
              <span className="font-medium text-primary">DNI:</span>
              <span className="ml-2 text-secondary">{profile.dni || 'No especificado'}</span>
            </div>
            <div>
              <span className="font-medium text-primary">Ciudad:</span>
              <span className="ml-2 text-secondary">{profile.city || 'No especificada'}</span>
            </div>
            <div>
              <span className="font-medium text-primary">Miembro desde:</span>
              <span className="ml-2 text-secondary">
                {profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Sección de cambio de contraseña */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <LockClosedIcon className="w-6 h-6 text-primary-600" />
            <div>
              <h3 className="text-lg font-semibold text-primary">Seguridad</h3>
              <p className="text-sm text-secondary">Cambia tu contraseña para mantener tu cuenta segura</p>
            </div>
          </div>
          {!showPasswordSection && (
            <button
              onClick={() => setShowPasswordSection(true)}
              className="btn-primary py-2 px-4"
            >
              Cambiar contraseña
            </button>
          )}
        </div>

        {showPasswordSection && (
          <div className="mt-6 border-t pt-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-medium text-primary">Cambiar contraseña</h4>
              <button
                onClick={() => {
                  setShowPasswordSection(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError('');
                  setNewPasswordError('');
                  setConfirmPasswordError('');
                }}
                className="text-secondary hover:text-primary"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handlePasswordChange} className="space-y-4">
              {/* Contraseña actual */}
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  Contraseña actual
                </label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light-500" />
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="input-field pl-10 pr-10"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-light-500 hover:text-light-600"
                  >
                    {showCurrentPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Nueva contraseña */}
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light-500" />
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      if (newPasswordError) validateNewPassword(e.target.value);
                    }}
                    className={`input-field pl-10 pr-10 ${newPasswordError ? 'border-red-300 focus:ring-red-500 focus:border-red-300' : ''}`}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-light-500 hover:text-light-600"
                  >
                    {showNewPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                {newPasswordError && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                    {newPasswordError}
                  </p>
                )}
              </div>

              {/* Confirmar nueva contraseña */}
              <div>
                <label className="block text-sm font-medium text-primary mb-2">
                  Confirmar nueva contraseña
                </label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-light-500" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (confirmPasswordError) validateConfirmPassword(e.target.value);
                    }}
                    className={`input-field pl-10 pr-10 ${confirmPasswordError ? 'border-red-300 focus:ring-red-500 focus:border-red-300' : ''}`}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-light-500 hover:text-light-600"
                  >
                    {showConfirmPassword ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                  </button>
                </div>
                {confirmPasswordError && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                    {confirmPasswordError}
                  </p>
                )}
              </div>

              {/* Error general */}
              {passwordError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600 flex items-center">
                    <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
                    {passwordError}
                  </p>
                </div>
              )}

              {/* Botones */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  disabled={isChangingPassword}
                  className="flex-1 btn-primary py-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isChangingPassword ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Cambiando...</span>
                    </div>
                  ) : (
                    'Cambiar contraseña'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPasswordSection(false)}
                  className="btn-secondary py-2 px-4"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
